/**
 * Todas as strings da interface. Chaves em inglês, valores em pt-BR.
 * Placeholders `{x}` são substituídos por `fmt()` em ./index.ts.
 */
export const ptBR = {
  brand: {
    name: "FoxyPay",
    partnerPsav: "Lumx",
    partnerIp: "instituição de pagamento parceira",
    partnerIpCnpj: "Instituição de pagamento parceira · informada na tela de pagamento",
    legalFooter:
      "Conversão e envio executados por Lumx, autorizada pelo Banco Central. Pix recebidos na conta de instituição de pagamento parceira. A FoxyPay não custodia valores nem realiza câmbio.",
    legalQuote:
      "A conversão e o envio são executados por Lumx, autorizada pelo Banco Central. O Pix é recebido na conta de instituição de pagamento parceira. A FoxyPay não custodia valores nem realiza câmbio.",
    partnerDisclosureDefault:
      "A FoxyPay é uma empresa de tecnologia. A custódia de ativos virtuais, a conversão e o envio ao exterior são realizados por Lumx, prestadora de serviços de ativos virtuais autorizada pelo Banco Central do Brasil nos termos das Resoluções BCB 519, 520 e 521. Os pagamentos via Pix são recebidos na conta de instituição de pagamento parceira, instituição de pagamento autorizada. A FoxyPay não custodia valores, não realiza operações de câmbio e não movimenta recursos fora das contas dos parceiros.",
    termsVersionDefault: "2026-09-08",
    fiscal: "Os relatórios reúnem dados para suas obrigações fiscais. A FoxyPay não presta consultoria fiscal.",
  },

  common: {
    loading: "Carregando…",
    retry: "Tentar de novo",
    back: "Voltar",
    cancel: "Cancelar",
    save: "Salvar",
    close: "Fechar",
    continue: "Continuar",
    confirm: "Confirmar",
    open: "Abrir",
    download: "Baixar",
    copy: "Copiar",
    copied: "Código Pix copiado.",
    copyFailed: "Não foi possível copiar. Selecione o texto e copie manualmente.",
    search: "Buscar",
    filter: "Filtrar",
    all: "Todos",
    none: "Nenhum",
    yes: "Sim",
    no: "Não",
    empty: "Nada por aqui ainda.",
    optional: "opcional",
    required: "obrigatório",
    errorTitle: "Algo deu errado",
    errorGeneric: "Algo deu errado. Tente de novo.",
    unauthorized: "Sua sessão expirou. Entre de novo.",
    forbidden: "Você não tem permissão para esta ação.",
    notFound: "Não encontramos o que você procurou.",
    export: "Exportar CSV",
    exportCsvHint: "Baixa um CSV com todos os pagamentos do período.",
    dateFrom: "De",
    dateTo: "Até",
    apply: "Aplicar",
    note: "Nota",
    notePlaceholder: "Escreva o motivo. Vai para a auditoria.",
    nRecords: "{n} registros",
    readonlyHint: "Seu papel é somente leitura. As ações ficam ocultas.",
    actions: "Ações",
    status: "Status",
    date: "Data",
    since: "Desde",
    at: "em",
    by: "por",
    total: "Total",
    version: "versão {v}",
    page: "Página {p}",
  },

  nav: {
    painel: "Painel",
    pagamentos: "Pagamentos",
    fornecedores: "Fornecedores",
    documentos: "Documentos",
    configuracoes: "Configurações",
    sair: "Sair",
    toca: "Toca",
    tocaFila: "Fila",
    tocaPedidos: "Pedidos",
    tocaOnboarding: "Onboarding",
    tocaFornecedores: "Fornecedores",
    tocaReconciliacao: "Reconciliação",
    tocaReconciliacaoShort: "Reconc.",
    tocaConfiguracoes: "Configurações",
    tocaAuditoria: "Auditoria",
    tocaAcessos: "Acessos",
    more: "Mais",
    goToPanel: "Ir para o Painel",
    goToToca: "Ir para a Toca",
  },

  roles: {
    owner: "Dono",
    finance: "Financeiro",
    viewer: "Leitura",
    admin: "admin",
    ops: "ops",
    compliance: "compliance",
    readonly: "leitura",
  },

  states: {
    quoted: "Cotação travada",
    awaiting_funds: "Aguardando seu Pix",
    funds_received: "Pix recebido",
    executing: "Enviando ao fornecedor",
    sent: "Enviado",
    settled: "Pago",
    failed: "Não concluído",
    refunded: "Devolvido",
    expired: "Cotação expirou",
    under_review: "Em análise",
  } as Record<string, string>,

  stateMessages: {
    quoted: "Cotação travada. Pague o Pix dentro do prazo mostrado na tela.",
    awaiting_funds: "Pague o valor exato na chave abaixo. Valor diferente entra em revisão manual.",
    funds_received: "Recebemos seu Pix na conta da instituição de pagamento parceira. Iniciando o envio ao fornecedor.",
    executing: "A Lumx está convertendo e enviando. Tempo típico: 4 a 20 minutos.",
    sent: "Saiu para o fornecedor. Hash da transação disponível na linha do tempo.",
    settled: "Chegou. Comprovante disponível.",
    failed: "O envio não foi executado. Seu dinheiro está sendo devolvido ao Pix de origem. Nenhuma taxa foi cobrada.",
    refunded: "Valor devolvido à conta de origem.",
    expired: "O Pix não chegou dentro do prazo. Nada foi cobrado. Peça uma nova cotação.",
    under_review: "Nossa equipe está verificando este pagamento. Prazo: até 1 dia útil. Você recebe e-mail quando mudar.",
  } as Record<string, string>,

  events: {
    quote_locked: "Cotação travada",
    pix_charge_created: "Aguardando seu Pix",
    pix_received: "Pix recebido",
    pix_divergent: "Pix com valor divergente",
    quote_expired: "Cotação expirou",
    cancelled: "Pedido cancelado",
    execution_started: "Enviando ao fornecedor",
    execution_sent: "Enviado",
    execution_settled: "Pago",
    execution_failed: "Não concluído",
    refund_completed: "Devolvido",
    review_opened: "Em análise",
    review_approved: "Análise aprovada",
    review_rejected: "Análise reprovada",
    late_pix_received: "Pix recebido após o prazo",
    partner_stuck: "Conversão demorando no parceiro",
    refund_manual_required: "Devolução em andamento pela equipe",
  } as Record<string, string>,

  eventDesc: {
    quote_locked: "1 USD = {rate} · custo total {total} · válida por 60 s.",
    pix_charge_created: "Pix gerado na conta da instituição de pagamento parceira com o valor exato {total}.",
    pix_received: "Pix de {paid} recebido na conta da instituição de pagamento parceira. Valor exato.",
    pix_divergent: "Pix recebido com {paid}, diferente do valor travado {total}. O envio não foi iniciado. Nossa equipe está verificando.",
    quote_expired: "O Pix não chegou dentro do prazo. Nada foi cobrado. Peça uma nova cotação.",
    cancelled: "Você cancelou antes de pagar. Nada foi cobrado.",
    execution_started: "A Lumx está convertendo e enviando. Tempo típico: 4 a 20 minutos.",
    execution_sent: "Saiu para o fornecedor. Hash {hash}.",
    execution_settled: "Chegou. Comprovante disponível.",
    execution_failed: "O envio não foi executado pelo parceiro ({code}). Seu dinheiro está sendo devolvido ao Pix de origem. Nenhuma taxa foi cobrada.",
    refund_completed: "{total} devolvidos à conta de origem.",
    review_opened: "Nossa equipe está verificando este pagamento. Prazo: até 1 dia útil.",
    review_approved: "Análise concluída. O envio segue.",
    review_rejected: "Análise reprovada. Seu dinheiro será devolvido ao Pix de origem.",
    late_pix_received: "Seu Pix de {paid} chegou depois da expiração. O valor está sendo devolvido ao Pix de origem. Nenhuma taxa foi cobrada.",
    partner_stuck: "Seu Pix chegou e a conversão no parceiro está demorando mais que o normal. Nossa equipe está acompanhando; nada mais precisa ser feito.",
    refund_manual_required: "A devolução de {paid} ao Pix de origem está sendo feita pela nossa equipe. Você recebe e-mail quando concluir.",
  } as Record<string, string>,

  actors: {
    client_user: "Você",
    operator: "Equipe FoxyPay",
    system: "FoxyPay",
    partner_vasp: "Lumx",
    partner_pix: "instituição de pagamento parceira",
    partner_kyb: "Parceiro de verificação",
  } as Record<string, string>,

  reviewReasons: {
    new_beneficiary_above_threshold: "Primeiro pagamento a este fornecedor acima do limite",
    sanctioned_country: "País com restrição",
    pix_amount_divergent: "Pix com valor divergente",
    beneficiary_bank_data_changed: "Dados do fornecedor alterados há menos de 48 h",
    partner_flagged: "Sinalizado pelo parceiro",
    pix_after_expiry: "Pix recebido depois da expiração (devolução automática)",
    beneficiary_rail_changed: "Fornecedor mudou os dados de recebimento entre a criação e a execução",
    refund_manual: "Devolução sendo feita pela equipe",
  } as Record<string, string>,

  legal: {
    terms: "Termos de uso",
    privacy: "Política de privacidade",
    navLabel: "Documentos legais",
    backToSignIn: "Voltar para o login",
  },

  auth: {
    signInTitle: "Entrar no Painel",
    signInLead: "Esperto é ver o custo antes de pagar.",
    email: "E-mail",
    password: "Senha",
    passwordHint: "Mínimo de 10 caracteres.",
    name: "Seu nome",
    signIn: "Entrar",
    signingIn: "Entrando…",
    noAccount: "Ainda não tem conta?",
    createAccount: "Criar conta",
    haveAccount: "Já tem conta?",
    createTitle: "Criar conta",
    createLead: "Estamos em beta fechado. Você pede o acesso agora; a equipe FoxyPay libera e avisa por e-mail. Depois disso você cadastra a empresa.",
    creating: "Enviando…",
    created: "Conta criada. Agora entre com seu e-mail e senha.",
    requestAccess: "Pedir acesso",
    requestedTitle: "Pedido recebido",
    requestedLead: "Seu cadastro entrou na fila do beta fechado. Assim que a equipe liberar, você recebe um e-mail em {email} e já consegue entrar com a senha que acabou de criar.",
    requestedBack: "Voltar para a página inicial",
    betaBadge: "Beta fechado",
    accountPending: "Seu cadastro ainda está em análise. Avisamos por e-mail assim que o acesso for liberado.",
    accountRejected: "Este cadastro não foi aprovado para o beta. Fale com oi@foxypay.com.br.",
    totpTitle: "Código do autenticador",
    totpLead: "Digite o código de 6 dígitos do seu aplicativo autenticador.",
    totpEnrollTitle: "Ative a verificação em duas etapas",
    totpEnrollLead: "Escaneie o QR no seu aplicativo autenticador (Google Authenticator, 1Password, Authy) e digite o código gerado.",
    totpSecret: "Ou digite o segredo manualmente",
    totpOpenApp: "Abrir no app autenticador",
    totpCopy: "Copiar segredo",
    totpCopied: "Segredo copiado",
    totpSamePhone: "No celular: toque em abrir para cadastrar direto no Google Authenticator, Authy ou no app Senhas do iPhone. Sem o app, copie o segredo e cole em \"Inserir chave de configuração\".",
    totpCode: "Código de 6 dígitos",
    totpVerify: "Confirmar",
    totpVerifying: "Confirmando…",
    totpInvalid: "Código inválido. Confira o relógio do celular e tente de novo.",
    rateLimited: "Muitas tentativas. Aguarde 15 minutos e tente de novo.",
    invalidCredentials: "E-mail ou senha incorretos.",
    weakPassword: "A senha precisa ter pelo menos 10 caracteres.",
    invalidEmail: "Digite um e-mail válido.",
    fillAll: "Preencha todos os campos.",
    logout: "Sair",
    accessAccountant: "Acesso contábil",
    forgotPassword: "Esqueci minha senha",
    lostAuthenticator: "Perdi meu autenticador",
    forgotTitle: "Redefinir senha",
    forgotLead: "Digite o e-mail da sua conta. Se ele existir, enviamos um link que vale por 1 hora.",
    forgotSend: "Enviar link",
    forgotSent: "Se o e-mail estiver cadastrado, o link chega em instantes. Confira também a pasta de spam.",
    resetTitle: "Escolha uma senha nova",
    resetLead: "Mínimo de 10 caracteres. As outras sessões da sua conta serão encerradas.",
    newPassword: "Nova senha",
    confirmPassword: "Repita a nova senha",
    passwordsDiffer: "As senhas não conferem.",
    resetSubmit: "Salvar senha",
    resetDone: "Senha alterada. Entre de novo com a senha nova.",
    tokenMissing: "Link incompleto. Abra o link exatamente como veio no e-mail.",
    lostTotpTitle: "Recuperar o autenticador",
    lostTotpLead: "Sem o aplicativo autenticador você não entra. Enviamos um link por e-mail; com ele e sua senha, o próximo login cadastra o autenticador de novo.",
    resetTotpTitle: "Reconfigurar o autenticador",
    resetTotpLead: "Confirme sua senha. Depois, entre de novo e cadastre o autenticador no seu celular novo.",
    resetTotpSubmit: "Confirmar e reconfigurar",
    resetTotpDone: "Pronto. Entre com e-mail e senha; o próximo passo mostra o QR Code do autenticador.",
    backToSignIn: "Voltar para o login",
  },

  onboarding: {
    title: "Cadastro da empresa",
    stepOf: "Etapa {n} de {total}",
    steps: ["Empresa", "Termos"],
    cnpj: "CNPJ",
    cnpjPlaceholder: "00.000.000/0000-00",
    cnpjInvalid: "CNPJ inválido. Confira os 14 dígitos.",
    lookup: "Consultar CNPJ",
    lookingUp: "Consultando a Receita…",
    companyFound: "Empresa encontrada",
    legalName: "Razão social",
    cnae: "CNAE",
    receitaStatus: "Situação na Receita",
    tradeName: "Nome fantasia (opcional)",
    address: "Endereço da empresa",
    addressLine1: "Rua e número",
    addressCity: "Cidade",
    addressState: "UF",
    addressPostalCode: "CEP",
    phone: "Telefone da empresa",
    phoneHint: "Com DDD, só números. Ex.: 11999990000",
    website: "Site da empresa",
    websiteHint: "Com https://. O parceiro exige um site; se não tiver, use a página no Instagram ou no marketplace.",
    companyType: "Tipo de empresa",
    companyTypes: {
      WHOLESALE_OF_DURABLE_GOODS: "Atacado / importação de bens duráveis (eletrônicos, máquinas, peças)",
      WHOLESALE_OF_NON_DURABLE_GOODS: "Atacado / importação de bens não duráveis (alimentos, cosméticos, insumos)",
      RETAILER_OF_DURABLE_GOODS: "Varejo de bens duráveis (loja, marketplace)",
      RETAILER_OF_NON_DURABLE_GOODS: "Varejo de bens não duráveis",
      CONSTRUCTION_OR_SKILLED_TRADE_BUSINESSES: "Construção ou serviços técnicos",
      PROFESSIONAL_SERVICES: "Serviços profissionais",
      OTHER: "Outro",
    } as Record<string, string>,
    annualRevenue: "Faturamento anual (aprox.)",
    annualRevenueOptions: { "10000000": "Até US$ 100 mil", "30000000": "US$ 100 mil a 500 mil", "100000000": "US$ 500 mil a 2 milhões", "500000000": "US$ 2 a 10 milhões", "2000000000": "Acima de US$ 10 milhões" } as Record<string, string>,
    partnerCompanyFields: "Sócios, documentos e dados da atividade você envia direto ao parceiro regulado (Lumx), no link que aparece depois dos termos.",
    uboAddress: "Endereço residencial do sócio (opcional; se vazio, usa o da empresa)",
    ubosTitle: "Sócios e beneficiários finais",
    ubosLead: "Informe todas as pessoas com 25% ou mais do capital, ou quem exerce o controle. A soma não pode passar de 100%.",
    uboName: "Nome completo",
    uboCpf: "CPF",
    uboShare: "Participação (%)",
    uboAdd: "Adicionar sócio",
    uboRemove: "Remover",
    uboInvalidCpf: "CPF inválido.",
    uboSumExceeded: "A soma das participações passa de 100%.",
    uboAtLeastOne: "Informe pelo menos um sócio.",
    uboBirthDate: "Nascimento",
    uboEmail: "E-mail do sócio",
    uboInvalidBirth: "Data de nascimento inválida (sócio precisa ter 18 anos ou mais).",
    uboInvalidEmail: "E-mail do sócio inválido.",
    uboPartnerFields: "Nascimento e e-mail são exigidos pelo parceiro regulado para verificar cada sócio.",
    uboInvalidAddress: "Endereço residencial incompleto: confira a UF (2 letras) e o CEP (8 dígitos).",
    incorporationDate: "Data de constituição",
    incorporationDateHint: "Data de abertura no cartão CNPJ. O parceiro regulado exige.",
    docsTitle: "Documentos de {company}",
    docKinds: {
      contrato_social: "Contrato social consolidado",
      comprovante_endereco: "Comprovante de endereço da empresa",
      documento_socio: "Documento dos sócios",
      documento_socio_verso: "Verso do documento dos sócios",
      outro: "Outro documento",
    } as Record<string, string>,
    docHints: {
      contrato_social: "PDF até 10 MB · última alteração registrada na Junta",
      comprovante_endereco: "Conta de luz, água ou telefone dos últimos 90 dias",
      documento_socio: "RG ou CNH (frente) de cada sócio",
      documento_socio_verso: "Verso do RG ou CNH de cada sócio (opcional se a frente já mostra os dois lados)",
      outro: "Qualquer documento adicional que ajude na verificação",
    } as Record<string, string>,
    docSocio: "Documento de {name} (sócio, {pct}%)",
    docSocioHint: "RG ou CNH, frente (ou frente e verso numa página só)",
    docSocioVerso: "Verso do documento de {name}",
    docSocioVersoHint: "Opcional: verso do RG ou CNH, se não estiver na mesma imagem da frente",
    sendFile: "Enviar arquivo",
    sending: "Enviando…",
    sent: "Enviado",
    replace: "Trocar",
    docRequired: "Envie o contrato social e o comprovante de endereço para continuar.",
    fileTooBig: "Arquivo acima de 10 MB.",
    fileType: "Aceitamos PDF, PNG ou JPG.",
    activityTitle: "Atividade da empresa",
    activityDescription: "O que a empresa faz",
    activityDescriptionPh: "Ex.: comércio varejista de eletrônicos via marketplaces",
    activityProducts: "Produtos ou serviços que importa",
    activityProductsPh: "Ex.: acessórios de celular, fones, cabos",
    activityCountries: "Países dos fornecedores",
    activityCountriesHint: "Separe por vírgula. Ex.: CN, US",
    activityVolume: "Volume mensal estimado (USD)",
    activityVolumeHighNote: "Acima de US$ 100 mil por mês o parceiro exige documentos financeiros (extrato, balanço ou IR) para liberar limite maior; envie na etapa Documentos como 'Outro documento'.",
    termsTitle: "Termos de uso e política de privacidade",
    termsAccept: "Li e aceito os termos de uso (versão {v}) e a política de privacidade.",
    termsMustAccept: "Você precisa aceitar os termos para enviar o cadastro.",
    termsReadFull: "Leia a versão completa:",
    submit: "Enviar cadastro para verificação",
    submitting: "Enviando…",
    continueTo: "Continuar para {step}",
    saveLater: "Você pode salvar e continuar depois",
    whatNext: "O que acontece depois",
    whatNext1: "A verificação da empresa e dos sócios é feita pela Lumx, parceira autorizada pelo Banco Central, no link que aparece depois dos termos. Os documentos vão direto para ela; a FoxyPay guarda só o resultado.",
    whatNext2: "Prazo típico: até 1 dia útil. Se faltar algo, você recebe e-mail com o que precisa.",
    whatNext3: "Limites iniciais: R$ 150 mil por pagamento e R$ 500 mil por mês, ajustáveis depois do primeiro mês.",
    privacy: "Privacidade",
    privacyText: "Dados e documentos dos sócios são enviados direto à Lumx para a verificação exigida por lei (LGPD, base legal: obrigação regulatória). A FoxyPay não os recebe.",
    statusTitle: {
      submitted: "Cadastro enviado",
      in_review: "Cadastro em análise",
      approved: "Empresa aprovada",
      needs_more_info: "Precisamos de mais informações",
      rejected: "Cadastro não aprovado",
      blocked: "Acesso bloqueado",
      draft: "Cadastro incompleto",
    } as Record<string, string>,
    statusBody: {
      submitted: "Recebemos seus dados. Agora conclua a verificação no link da Lumx abaixo.",
      in_review: "Conclua a verificação no link da Lumx abaixo, se ainda não fez. Depois do envio, o prazo típico é de até 2 dias úteis. Você recebe e-mail quando mudar.",
      approved: "Tudo certo. Você já pode cadastrar fornecedores e pedir a primeira cotação.",
      needs_more_info: "A Lumx pediu mais informações. Veja abaixo o que falta e reenvie pelo link da verificação.",
      rejected: "Não foi possível aprovar a empresa neste momento. Se tiver dúvidas, fale com a gente pelo e-mail de suporte.",
      blocked: "O acesso desta empresa está bloqueado. Fale com o suporte.",
      draft: "Continue de onde parou.",
    } as Record<string, string>,
    partnerStepsTitle: "Concluir no parceiro regulado",
    partnerStepsLead: "Aceite os termos da Lumx e faça a verificação da empresa e dos sócios no fluxo guiado dela. O status aqui atualiza sozinho.",
    reasonsTitle: "O que precisamos",
    flagsTitle: "Sinalizações",
    resume: "Continuar cadastro",
    resubmit: "Revisar e reenviar",
    checkStatus: "Atualizar status",
    goPanel: "Ir para o Painel",
    sandboxDecide: "Demonstração · simular decisão do parceiro",
    approve: "Aprovar",
    reject: "Rejeitar",
    needsInfo: "Pedir mais informações",
    viewerCannot: "Só o dono ou o financeiro podem editar o cadastro.",
  },

  panel: {
    title: "Painel",
    kpiPaid: "Pago este mês",
    kpiPaidSub: "{n} pagamentos · {usd}",
    kpiOngoing: "Em andamento",
    kpiOngoingSub: "{sending} enviando · {review} em análise",
    kpiSavings: "Economia vs. banco",
    kpiSavingsSub: "este mês, comparação ilustrativa",
    kpiLimit: "Limite disponível",
    kpiLimitSub: "de {monthly} por mês",
    recent: "Pagamentos recentes",
    recentMobile: "Recentes",
    seeAll: "Ver todos",
    newPayment: "Novo pagamento",
    colSupplier: "Fornecedor",
    colUsd: "Valor",
    colBrl: "Pago via Pix",
    colStatus: "Status",
    colDate: "Data",
    emptyTitle: "Nenhum pagamento ainda",
    emptyBody: "Cadastre um fornecedor e peça a primeira cotação. Você vê o custo total antes de pagar.",
    monthLabel: "{month} · {company}",
    paid: "pagos",
  },

  payments: {
    title: "Pagamentos",
    lead: "Todos os pedidos da empresa. Filtre por status e período ou busque por fornecedor, referência ou número.",
    searchPh: "Fornecedor, referência ou FXP-…",
    status: "Status",
    allStatuses: "Todos os status",
    from: "De",
    to: "Até",
    clear: "Limpar filtros",
    total: "{n} pedido(s)",
    empty: "Nenhum pedido com esses filtros.",
    colId: "Pedido",
    colSupplier: "Fornecedor",
    colUsd: "Valor",
    colBrl: "Pix",
    colStatus: "Status",
    colDate: "Criado",
    prev: "Anterior",
    next: "Próxima",
    pageOf: "Página {p} de {n}",
    exportHint: "Exporta CSV com o período filtrado.",
  },

  notifications: {
    title: "Notificações",
    bell: "Notificações",
    unread: "{n} não lida(s)",
    empty: "Nenhuma notificação ainda. Você recebe aviso a cada passo do pagamento.",
    markAll: "Marcar todas como lidas",
    markedAll: "Tudo lido.",
    openOrder: "Abrir pedido",
    newTag: "Nova",
  },

  newPayment: {
    title: "Novo pagamento",
    crumb: "Pagamentos / Novo",
    formTitle: "Dados do pagamento",
    supplier: "Fornecedor",
    supplierPlaceholder: "Escolha um fornecedor ativo",
    supplierNone: "Nenhum fornecedor ativo. Cadastre um em Fornecedores.",
    supplierChange: "Trocar",
    amount: "Valor para o fornecedor",
    amountInvalid: "Informe um valor maior que zero.",
    nature: "Natureza da operação",
    natures: { goods_import: "Importação de mercadoria", service_import: "Serviço contratado no exterior" } as Record<string, string>,
    invoice: "Invoice ou proforma (obrigatório)",
    invoiceNone: "Nenhum arquivo anexado",
    invoiceAttach: "Anexar PDF",
    invoiceReplace: "Trocar",
    invoiceUploading: "Enviando…",
    invoiceAttached: "{name} · {size} · anexado agora",
    invoiceRequired: "Anexe a invoice ou proforma para pedir a cotação.",
    reference: "Referência interna (opcional)",
    referencePh: "Ex.: reposição set/26, lote 3",
    travelRule: "Os dados do originador e do beneficiário vão para o parceiro regulado junto com a transferência (Travel Rule). Você não precisa preencher nada a mais.",
    askQuote: "Pedir cotação",
    newQuote: "Nova cotação",
    sniffing: "Farejando a cotação…",
    sniffingBody: "Pedindo RFQ ao parceiro regulado.",
    quoteIdle: "Preencha os dados e peça a cotação.",
    quoteIdleBody: "Você vai ver o câmbio base, a Taxa FoxyPay, a tarifa de destino e o IOF antes de confirmar. A cotação fica travada enquanto você paga o Pix.",
    quoteReady: "Foxy farejou a cotação",
    quoteUpdatedAt: "atualizada {time}",
    rowBase: "Câmbio base",
    rowBaseCalc: "{usd} × {rate}",
    ptaxRef: "PTAX venda {rate}",
    rowFee: "Taxa FoxyPay ({pct})",
    rowFeeSub: "já inclui conversão e rede do parceiro",
    planBeta: "preço do beta",
    planCustom: "preço negociado",
    rowIof: "IOF ({pct})",
    rowDestination: "Tarifa de destino",
    rowDestinationNamed: "Tarifa de destino ({network})",
    destinationIncluded: "incluída",
    youPay: "Você paga via Pix",
    vet: "VET equivalente",
    vetTooltip: "Calculado como o VET da operação de câmbio, para você comparar direto. Neste trilho não há contrato de câmbio bancário; a execução é por prestador de serviços de ativos virtuais parceiro.",
    locked: "Cotação travada",
    bankCompare: "No banco, este pagamento custaria cerca de {bank}. Você paga {savings} a menos.",
    bankDisclaimerDefault: "Banco, estimativa de mercado: spread de 4%, IOF de 0,38% e tarifa de R$ 150. Não é uma cotação de banco.",
    generatePix: "Gerar Pix de {total}",
    generating: "Gerando Pix…",
    invalidated: "Você alterou os dados. Peça uma nova cotação.",
    expired: "A cotação expirou. Nada foi cobrado. Peça uma nova cotação.",
    nothingCharged: "Nenhum valor foi cobrado.",
    limitExceeded: "Este valor passa do seu limite ({which}). Reduza o valor ou fale com a gente para ajustar.",
    limitPerTx: "por pagamento",
    limitMonthly: "mensal",
    quoteExpiredOnCreate: "A cotação expirou antes de gerar o Pix. Peça uma nova.",
    beneficiaryNotActive: "Este fornecedor ainda não está ativo.",
    partnerRejected: "O parceiro não aceitou esse valor. Ajuste o valor ou fale com a gente.",
  },

  pix: {
    title: "Pague o Pix",
    payExactly: "Pague exatamente",
    validFor: "Pix válido por",
    copyPaste: "Pix copia e cola",
    payee: "Favorecido: {payee}. O Pix vai para a conta da instituição de pagamento parceira, nunca para a FoxyPay.",
    divergent: "Pagamentos com valor diferente do exato entram em revisão manual e podem atrasar o envio.",
    order: "Pedido {id}",
    lockedCost: "Custo travado",
    totalPix: "Total do Pix",
    cancelOrder: "Cancelar pedido",
    cancelConfirm: "Cancelar este pedido? Nada foi cobrado.",
    cancelled: "Pedido cancelado. Nada foi cobrado.",
    summary: "Resumo",
    supplier: "Fornecedor",
    sending: "Envio",
    parity: "Paridade",
    lockedRate: "Câmbio travado",
    invoice: "Invoice",
    qrAlt: "QR Code do Pix",
    expiredBanner: "O prazo do Pix acabou. Nada foi cobrado.",
    fundedBanner: "Pix recebido. O parceiro está convertendo e enviando ao fornecedor; isso leva alguns minutos e esta tela atualiza sozinha.",
    waiting: "Aguardando o Pix chegar na conta da instituição de pagamento parceira. Esta tela atualiza sozinha.",
  },

  order: {
    title: "Pedido",
    crumb: "Pagamentos / {id}",
    sub: "Pedido {id} · {supplier}",
    paidWith: "pago com {brl} via Pix",
    quotedAt: "cotação de {brl}",
    timeline: "Linha do tempo",
    documents: "Documentos",
    details: "Detalhes",
    lockedRate: "Câmbio travado",
    effectiveRate: "Câmbio efetivo",
    totalCost: "Custo total",
    nature: "Natureza",
    hash: "Hash",
    bankRef: "Referência bancária",
    fxContract: "Contrato de câmbio",
    reference: "Referência",
    docReceipt: "Comprovante de pagamento",
    docReceiptSub: "PDF · com hash e câmbio efetivo",
    docFx: "Contrato de câmbio",
    docFxSub: "PDF · emitido por Lumx",
    docFxPending: "Chega em até 1 dia útil",
    docInvoice: "Invoice {name}",
    docInvoiceSub: "anexada por você",
    downloadAll: "Baixar tudo",
    repeat: "Repetir pagamento",
    newQuote: "Pedir nova cotação",
    backToPanel: "Voltar ao painel",
    bannerReview: "Nossa equipe está verificando este pagamento. O valor recebido ficou na conta da instituição de pagamento parceira e nenhuma execução foi iniciada. Você recebe e-mail quando mudar. Prazo: até 1 dia útil.",
    bannerFailed: "O envio não foi executado. Seu dinheiro está sendo devolvido ao Pix de origem pela instituição de pagamento parceira. Nenhuma taxa foi cobrada.",
    bannerExpired: "O Pix não chegou dentro do prazo. Nada foi cobrado. Peça uma nova cotação.",
    bannerRefunded: "Valor devolvido à conta de origem. Nenhuma taxa foi cobrada.",
    bannerFollowing: "Acompanhando junto ao parceiro. Esta tela atualiza sozinha.",
    npsTitle: "Como foi pagar com a FoxyPay?",
    npsThanks: "Obrigado. Sua nota ({n}) foi registrada.",
    npsLow: "Nada provável",
    npsHigh: "Muito provável",
    reviewReasons: "Motivos da análise",
    notFound: "Não encontramos este pedido.",
    stepper: ["quoted", "awaiting_funds", "funds_received", "executing", "settled"],
  },

  suppliers: {
    edit: "Editar",
    editTitle: "Editar fornecedor",
    editLead: "Mudar carteira ou conta bloqueia enquanto houver pagamento em andamento e leva o fornecedor a uma nova verificação.",
    saveChanges: "Salvar alterações",
    saved: "Fornecedor atualizado.",
    noChanges: "Nada foi alterado.",
    title: "Fornecedores",
    new: "Novo fornecedor",
    searchPh: "Buscar por nome, país ou carteira",
    count: "{n} fornecedores",
    colName: "Fornecedor",
    colRail: "Recebe em",
    colDest: "Destino",
    colStatus: "Status",
    colHistory: "Histórico",
    pay: "Pagar",
    seeReview: "Ver revisão",
    seeReason: "Ver motivo",
    status: { active: "Ativo", pending_review: "Em revisão", blocked: "Bloqueado" } as Record<string, string>,
    payments: "{n} pagamentos",
    payment1: "1 pagamento",
    noPayments: "nenhum pagamento",
    changedAt: "dados alterados em {date}",
    flagged: "sinalizado pelo parceiro",
    legalName: "Nome legal",
    country: "País",
    address: "Endereço",
    document: "Documento (tax ID, opcional)",
    relationship: "Relação",
    relationships: { goods: "Mercadoria", service: "Serviço" } as Record<string, string>,
    howReceives: "Como ele recebe",
    railStable: "Carteira stablecoin",
    railBank: "Conta bancária",
    asset: "Ativo",
    network: "Rede",
    networks: { tron: "Tron (TRC-20)", ethereum: "Ethereum (ERC-20)", polygon: "Polygon", solana: "Solana" } as Record<string, string>,
    walletAddress: "Endereço da carteira",
    walletValid: "Formato válido para {network}. O endereço passa por checagem do parceiro antes do primeiro pagamento.",
    walletInvalid: "Endereço fora do formato da rede {network}. Confira com o fornecedor.",
    bankCountry: "País do banco",
    scheme: "Como a conta recebe",
    schemes: { ach: "ACH · conta em dólar nos EUA", fedwire: "Fedwire · conta em dólar nos EUA", swift: "SWIFT · conta em dólar (Hong Kong, China e outros)", cnaps: "CNAPS (China)" } as Record<string, string>,
    schemeSla: { ach: "1 a 2 dias úteis", fedwire: "mesmo dia útil (até 15h ET)", swift: "1 a 5 dias úteis", cnaps: "indisponível" } as Record<string, string>,
    slaStable: "minutos",
    slaLabel: "Prazo",
    costNote: "O custo do trilho aparece na cotação, linha \"Tarifa\": não há tarifa fixa cadastrada aqui.",
    schemeHint: { ach: "Prazo de 1 a 2 dias úteis após o Pix. Custo na cotação.", fedwire: "Mesmo dia útil quando o Pix cai antes das 15h (horário de Nova York). Custo na cotação.", swift: "Prazo de 1 a 5 dias úteis; bancos correspondentes podem descontar tarifa no caminho. Custo na cotação." } as Record<string, string>,
    bankName: "Banco",
    bankAddress: "Endereço do banco",
    accountNumber: "Número da conta",
    accountType: "Tipo de conta",
    accountTypes: { checking: "Corrente (checking)", savings: "Poupança (savings)" } as Record<string, string>,
    routing: "Routing number (ABA)",
    routingInvalid: "Routing number tem 9 dígitos e dígito verificador. Confira com o fornecedor.",
    swiftBic: "SWIFT / BIC",
    bicInvalid: "BIC tem 8 ou 11 caracteres (ex.: HSBCHKHH).",
    iban: "IBAN (quando o banco usa)",
    ibanInvalid: "IBAN fora do formato. Deixe em branco se o banco usa número de conta.",
    ibanOrAccount: "Informe o IBAN ou o número da conta.",
    cnapsCode: "Código CNAPS",
    accountHolder: "Titular da conta",
    holderSection: "Titular (o fornecedor)",
    holderEmail: "E-mail do titular",
    holderEmailInvalid: "E-mail do titular inválido.",
    addrLine1: "Endereço (rua e número)",
    addrCity: "Cidade",
    addrState: "Estado / província",
    addrPostalCode: "CEP / código postal",
    documentRequiredBank: "Para conta bancária, o documento (tax ID) do fornecedor é obrigatório.",
    maskedAccount: "Digite o número da conta completo para alterar os dados bancários.",
    submit: "Cadastrar fornecedor",
    submitting: "Cadastrando…",
    created: "Fornecedor cadastrado. Ele passa por checagem do parceiro antes do primeiro pagamento.",
    fillAll: "Preencha nome, país, endereço e os dados de recebimento.",
    reviewInfo: "Este fornecedor está em revisão. Pagamentos ficam disponíveis quando a checagem terminar.",
    blockedInfo: "Este fornecedor foi bloqueado pelo parceiro na checagem. Não é possível pagar.",
    flagsTitle: "Sinalizações",
    empty: "Nenhum fornecedor ainda. Cadastre o primeiro ao lado.",
    bankRailOff: "Conta bancária ainda não está disponível.",
    countryPh: "Ex.: CN, US",
    countryHint: "Código ISO de 2 letras",
  },

  documents: {
    title: "Documentos",
    lead: "Comprovante, invoice e extrato. Nada de cofre.",
    statements: "Extrato mensal",
    statementDownload: "Baixar extrato de {month}",
    byOrder: "Por pedido",
    colOrder: "Pedido",
    colSupplier: "Fornecedor",
    colDocs: "Documentos",
    receipt: "Comprovante",
    fxContract: "Contrato de câmbio",
    invoice: "Invoice",
    empty: "Nenhum documento ainda. Eles aparecem aqui conforme os pagamentos acontecem.",
    exportAll: "Exportar CSV do período",
  },

  settings: {
    title: "Configurações",
    users: "Usuários da empresa",
    usersLead: "Dono cria e administra. Financeiro paga. Leitura (acesso contábil) só consulta.",
    colName: "Nome",
    colEmail: "E-mail",
    colRole: "Papel",
    newUser: "Novo usuário",
    userName: "Nome",
    userEmail: "E-mail",
    userRole: "Papel",
    userPassword: "Senha inicial",
    userPasswordHint: "Mínimo de 10 caracteres. A pessoa configura o autenticador no primeiro acesso.",
    create: "Criar usuário",
    creating: "Criando…",
    created: "Usuário criado.",
    onlyOwner: "Só o dono pode criar usuários.",
    limits: "Limites atuais",
    limitsLead: "Definidos pela FoxyPay depois da verificação. Ajustáveis depois do primeiro mês.",
    perTx: "Por pagamento",
    monthly: "Por mês",
    usedMonth: "Usado este mês",
    company: "Empresa",
    logout: "Sair da conta",
    loggingOut: "Saindo…",
    account: "Sua conta",
    version: "Versão do app",
    apiKeys: "Chaves de API",
    apiKeysLead: "Para integrar seu ERP. A chave age como usuário financeiro: cria fornecedores, cotações e pedidos e baixa documentos. Não administra usuários.",
    apiKeyName: "Nome da chave",
    apiKeyNameHint: "Ex.: ERP Bling, planilha do contador.",
    apiKeyCreate: "Gerar chave",
    apiKeyCreating: "Gerando…",
    apiKeySecretTitle: "Copie agora. Esta chave não aparece de novo.",
    apiKeySecretLead: "Guarde no cofre de segredos do seu sistema. Se perder, revogue e gere outra.",
    apiKeyCopy: "Copiar",
    apiKeyCopied: "Copiada.",
    apiKeyDone: "Já guardei",
    apiKeyRevoke: "Revogar",
    apiKeyRevokeConfirm: "Revogar a chave {name}? Integrações que a usam param na hora.",
    apiKeyRevoked: "Chave revogada.",
    apiKeyColName: "Nome",
    apiKeyColPrefix: "Prefixo",
    apiKeyColCreated: "Criada",
    apiKeyColLastUsed: "Último uso",
    apiKeyNeverUsed: "Nunca",
    apiKeyRevokedTag: "Revogada",
    apiKeyNone: "Nenhuma chave ainda.",
    apiKeyDocs: "Como usar: header Authorization: Bearer <chave> nas rotas de /api/v1. Contrato em docs/API.md.",
    onlyOwnerKeys: "Só o dono gera e revoga chaves.",
    companyData: "Dados da empresa",
    companyDataLead: "Razão social e CNPJ vêm da Receita e não mudam aqui. Nome fantasia e endereço aparecem no comprovante.",
    tradeName: "Nome fantasia",
    address: "Endereço",
    saveCompany: "Salvar dados",
    savedCompany: "Dados da empresa salvos.",
    security: "Segurança",
    securityLead: "Senha e autenticador. Trocar a senha encerra as outras sessões; reconfigurar o autenticador encerra todas.",
    currentPassword: "Senha atual",
    changePassword: "Trocar senha",
    passwordChanged: "Senha trocada. As outras sessões foram encerradas.",
    totpReconfigure: "Reconfigurar autenticador",
    totpReconfigureLead: "Trocou de celular? Confirme a senha; você sai agora e cadastra o autenticador no próximo login.",
    totpReconfigureConfirm: "Confirmar e sair",
    totpReconfigured: "Autenticador zerado. Entre de novo para cadastrar o novo.",
    push: "Notificações no navegador",
    pushLead: "Aviso na tela quando o Pix chega, o envio sai e o pagamento conclui. Vale para este navegador.",
    pushOn: "Ativar neste navegador",
    pushOff: "Desativar neste navegador",
    pushWorking: "Aguarde…",
    pushActive: "Ativas neste navegador.",
    pushInactive: "Desativadas.",
    pushDenied: "O navegador bloqueou as notificações. Libere nas configurações do site e tente de novo.",
    pushUnsupported: "Este navegador não suporta notificações push.",
    pushDisabled: "Ainda não habilitadas neste ambiente.",
    pushDevices: "{n} navegador(es) inscritos na sua conta.",
  },

  sandbox: {
    title: "Demonstração · simule o parceiro",
    state: "Estado: {state}",
    noOrder: "Abra um pedido aguardando Pix para simular.",
    payExact: "Pix recebido (valor exato)",
    payDivergent: "Pix com valor divergente",
    expire: "Deixar expirar",
    working: "Enviando ao sandbox…",
    done: "Simulação enviada. A tela atualiza em instantes.",
    failed: "O sandbox não respondeu: {msg}",
  },

  toca: {
    badge: "TOCA",
    queueTitle: "Fila de exceções",
    kpiExceptions: "Exceções abertas",
    kpiExceptionsSub: "{crit} críticas · {rev} para revisar",
    kpiRecon: "Reconciliação de hoje",
    kpiReconOk: "0 divergências",
    kpiReconIssues: "{n} divergências",
    kpiReconSub: "D-1 fechada às {time} · Pix {pix} · pedidos {orders} · execuções {exec}",
    kpiReconNone: "Nenhuma rodada ainda",
    kpiTiming: "Cotação → pago (p50 / p95)",
    kpiTimingSub: "últimos {days} dias · {n} pedidos",
    kpiTpv: "TPV do período",
    kpiTpvSub: "take rate efetivo {rate} · receita {revenue}",
    kpiStuck: "On-ramp parado",
    kpiStuckSubNone: "nenhum Pix preso no parceiro",
    kpiStuckSub: "Pix no parceiro há mais de {min} min sem converter",
    stuckTitle: "On-ramp parado no parceiro",
    stuckLead: "O Pix entrou na Lumx, mas a conversão não terminou em {min} min. O pedido não expira; confira o on-ramp no dashboard do parceiro e abra chamado se estiver travado.",
    stuckItem: "{amount} · há {min} min",
    stuckBanner: "Pix no parceiro há {age} sem a conversão terminar. O pedido segue em awaiting_funds e não expira; confira o on-ramp no dashboard da Lumx e abra chamado com o parceiro se estiver travado.",
    reasonRefundManual: "Devolução manual",
    reasonRefundManualDetail: "Parceiro não devolve sozinho · {amount} a devolver pelo dashboard",
    refundManualTitle: "Devolução manual necessária",
    refundManualBody: "A Lumx não devolve on-ramp automaticamente. Devolva {amount} ao Pix de origem pelo dashboard da Lumx e, depois, confirme aqui com \"Confirmar devolução manual\", informando a referência da devolução. Só então o pedido vira devolvido e o ledger é estornado.",
    refundManualConfirmHint: "Confirme só depois de a devolução constar no dashboard da Lumx. Esta ação registra o pedido como devolvido e estorna o ledger.",
    refundManualRef: "Referência da devolução no dashboard",
    refundManualRefPlaceholder: "ex.: id da devolução na Lumx, E2E do Pix de volta, data e valor",
    queueColOrder: "Pedido",
    queueColWho: "Fornecedor · cliente",
    queueColAmount: "Valor",
    queueColReason: "Motivo",
    queueColSince: "Desde",
    queueEmpty: "Nenhuma exceção aberta. A fila está limpa.",
    open: "Abrir",
    resolve: "Resolver",
    reasonDivergent: "Pix divergente",
    reasonFailed: "Reprocessar",
    reasonReview: "Em análise",
    reasonBlocked: "Bloqueado",
    reasonOther: "Exceção",
    queueFooter: "Toda ação aqui gera registro de auditoria com operador, horário e payload do parceiro. Nenhuma ação movimenta fundos: devoluções e reprocessamentos são ordens ao parceiro.",
    actions: {
      approve_review: "Aprovar revisão",
      reject_review: "Rejeitar revisão",
      start_refund: "Iniciar devolução ao cliente",
      reprocess: "Reprocessar",
      add_note: "Adicionar nota",
      confirm_manual_refund: "Confirmar devolução manual",
    } as Record<string, string>,
    actionDone: "Ação registrada: {action}.",
    actionConfirm: "Confirmar {action}?",
    noteRequired: "Escreva uma nota para esta ação.",
    whatHappened: "O que aconteceu",
    notes: "Notas",
    noNotes: "Nenhuma nota ainda.",
    kybTitle: "Onboarding · casos KYB",
    kybEmpty: "Nenhum caso nesta situação.",
    accessTitle: "Acessos ao beta",
    accessLead: "Cada cadastro novo espera sua liberação. Aprovar libera o login; rejeitar bloqueia e derruba sessões abertas.",
    accessStatus: { pending: "Aguardando", approved: "Liberados", rejected: "Rejeitados" } as Record<string, string>,
    accessEmpty: "Nenhum cadastro nesta situação.",
    accessColWho: "Pessoa",
    accessColCompany: "Empresa",
    accessColWhen: "Pedido em",
    accessDecide: "Decidir",
    accessDecision: { approved: "Liberar acesso", rejected: "Rejeitar" } as Record<string, string>,
    accessDecided: "Acesso {d}.",
    accessPendingPill: "Acessos {n}",
    leadsTitle: "Lista de espera do site",
    leadsLead: "Quem deixou e-mail e CNPJ no formulário do beta em foxypay.com.br. Marque como contatado depois de falar com a pessoa.",
    leadsEmpty: "Ninguém na lista ainda.",
    leadsColCnpj: "CNPJ",
    leadsColWhen: "Entrou em",
    leadsContacted: "Contatado",
    leadsMarkContacted: "Marcar contatado",
    leadsMarked: "Lead marcado como contatado.",
    leadsAnswers: { paysHow: "Paga hoje", supplier: "Fornecedor", perMonth: "Pagamentos/mês", avgAmount: "Valor médio", accountant: "Contador" } as Record<string, string>,
    kybColCompany: "Empresa",
    kybColFlags: "Sinalizações",
    kybColSubmitted: "Enviado",
    kybDecide: "Decidir",
    kybDecision: { approved: "Aprovar", rejected: "Rejeitar", needs_more_info: "Pedir mais informações" } as Record<string, string>,
    kybDecided: "Decisão registrada: {d}.",
    kybStatus: {
      draft: "Rascunho",
      submitted: "Enviado",
      in_review: "Em análise",
      approved: "Aprovado",
      rejected: "Rejeitado",
      needs_more_info: "Mais informações",
      blocked: "Bloqueado",
    } as Record<string, string>,
    benTitle: "Fornecedores · pendentes e sinalizados",
    benEmpty: "Nenhum fornecedor pendente.",
    benDecision: { active: "Ativar", blocked: "Bloquear" } as Record<string, string>,
    benDecided: "Decisão registrada: {d}.",
    ordersTitle: "Pedidos",
    ordersEmpty: "Nenhum pedido com estes filtros.",
    filterState: "Estado",
    filterCompany: "Empresa (id)",
    filterException: "Só exceções",
    orderTitle: "Pedido {id}",
    company: "Cliente",
    partnerPayloads: "Payloads do parceiro",
    ledger: "Ledger",
    ledgerEmpty: "Nenhum lançamento ainda.",
    events: "Eventos",
    pixCharge: "Cobrança Pix",
    execution: "Execução",
    reconTitle: "Reconciliação",
    reconRun: "Rodar agora",
    reconRunning: "Rodando…",
    reconRan: "Rodada iniciada. Atualize em instantes.",
    reconDay: "Dia",
    reconRuns: "Rodadas",
    reconIssues: "Divergências abertas",
    reconIssuesNone: "Nenhuma divergência aberta.",
    reconResolve: "Resolver",
    reconResolution: "Resolução",
    reconResolved: "Divergência resolvida.",
    reconMatch: "Batimento",
    reconPix: "Pix recebidos (IP)",
    reconOrders: "Pedidos funds_received",
    reconExec: "Execuções no parceiro",
    reconFees: "Taxas do parceiro",
    reconRevenue: "Receita FoxyPay",
    reconBalances: "Ledger interno",
    reconClosed: "Fechada",
    reconOpen: "Em andamento",
    settingsTitle: "Configurações",
    settingsLead: "Cada mudança cria uma nova versão. A vigente é a de maior versão.",
    settingKey: "Chave",
    settingValue: "Valor (JSON)",
    settingNote: "Nota da versão",
    settingSave: "Salvar nova versão",
    settingSaved: "Versão {v} salva.",
    settingInvalidJson: "JSON inválido: {msg}",
    settingHistory: "Histórico",
    companiesTitle: "Empresas · limites e take rate",
    colCnpj: "CNPJ",
    colPerTx: "Limite / pagamento",
    colMonthly: "Limite / mês",
    colTakeRate: "Taxa negociada (bp)",
    colBetaEnds: "Fim do beta",
    takeRateHint: "Taxa FoxyPay total em bp (80 = 0,8%). Vazio = tabela ou beta.",
    betaEndsHint: "Vazio = conta 90 dias a partir do primeiro pagamento entregue.",
    colBlocked: "Bloqueada",
    edit: "Editar",
    companySaved: "Empresa atualizada.",
    auditTitle: "Auditoria",
    auditTarget: "Alvo",
    auditActor: "Ator",
    auditAction: "Ação",
    auditMeta: "Metadados",
    auditEmpty: "Nenhum registro no filtro.",
    deadJobs: "Jobs mortos (DLQ)",
    retry: "Reprocessar job",
    notStaff: "Esta área é da equipe FoxyPay.",
  },
} as const;

export type Dictionary = typeof ptBR;
